/**
 * useScoreEditActions
 *
 * Edit-Aktionen der interaktiven Vorschau: Löschen/Teilen/Zusammenführen, Kopieren/Einfügen
 * (Strg+C/V/Umschalt+V), Bindebogen (Strg+B), Akkord, Tonart und Taktart. Alle Aktionen mutieren
 * nur den beschreibbaren `musicXml`-Ref – hasUnsavedChanges/Emit übernimmt `MusicXmlPreview`
 * (watch(musicXml)); Auswahl/Markierung bleiben und werden nach dem Re-Render wiederhergestellt.
 * Die pure XML-/Dauer-Logik lebt framework-frei in `@/composables/musicXmlDom` (direkt importiert).
 * Sub-Composable von {@link @/composables/useOsmdInteractiveScore}.
 */
import type {Ref} from 'vue';
import type {GraphicalNote} from 'opensheetmusicdisplay';
import type {
    BarStyleChoice,
    ChordToneExtreme,
    ClipboardNote,
    DiatonicEditResult,
    KeySignatureInfo,
    NoteInfo,
    TimeSignatureInfo,
    TriadReductionMode,
} from '@/composables/musicXmlDom';
import {
    addDiatonicChordTonesInMusicXml,
    deleteMeasuresInMusicXml,
    deleteNotesInMusicXml,
    findMeasureInPartByIndex,
    getParts,
    insertMeasureInMusicXml,
    insertSequenceAfterInMusicXml,
    markBassPreviewMeasuresAsEdited,
    measureIsOverfullForMeter,
    parseMusicXml,
    removeEndingInMusicXml,
    setBarStyleInMusicXml,
    setEndingInMusicXml,
    setRepeatInMusicXml,
    setRepeatTimesInMusicXml,
    mergeNoteWithNextInMusicXml,
    noteKey,
    pitchToMidi,
    readNotesPayload,
    readSoundingPitches,
    reduceTriadsInMusicXml,
    removeExtremeChordTonesInMusicXml,
    replaceNotesPitchesInMusicXml,
    replaceNoteWithSequenceInMusicXml,
    splitNoteInMusicXml,
    toggleTieOrSlurInMusicXml,
    transposeNotesInMusicXml,
    updateChordInMusicXml,
    updateKeySignatureInMusicXml,
    updateTimeSignatureInMusicXml,
} from '@/composables/musicXmlDom';
import {remapSelectionAfterIndexEdits} from '@/composables/osmd/selectionLogic';
import type {NotePreviewTone} from '@/composables/osmd/notePreviewTone';

export interface ScoreEditActionsDeps {
    musicXml: Ref<string | null>;
    selectedMeasureIndex: Ref<number | null>;
    selectedMeasure: Ref<number | null>;
    selectedNote: Ref<NoteInfo | null>;
    /** Mehrfachauswahl (enthält die primäre `selectedNote`); die Batch-Aktionen (delete/copy/
     *  transpose) operieren darauf, mit Einzelauswahl als Sonderfall (Liste der Größe 1). */
    selectedNotes: Ref<NoteInfo[]>;
    selectedMeasureIndices: Ref<number[]>;
    selectedMeasures: Ref<number[]>;
    clickedGraphicalNote: Ref<GraphicalNote | null>;
    /** Kopierte Sequenz (Mehrfach-Kopieren); Basis für das Sequenz-Einfügen. */
    copiedSequence: Ref<ClipboardNote[]>;
    /** Lässt die resultierende Tonhöhe hören (no-op in der Griffschrift-Ausgabe). */
    previewTone: NotePreviewTone;
    /** Einmal-Unterdrückung der Auto-Notenwahl des Takt-Watches in `useScoreSelection` –
     *  gesetzt von `insertMeasure`, wenn der Takt-Kontext programmatisch wandert. */
    suppressMeasureAutoSelect: Ref<boolean>;
}

export function useScoreEditActions(deps: ScoreEditActionsDeps) {
    const {
        musicXml, selectedMeasureIndex, selectedMeasure, selectedNote, selectedNotes,
        selectedMeasureIndices, selectedMeasures, clickedGraphicalNote,
        copiedSequence, previewTone, suppressMeasureAutoSelect,
    } = deps;

    /** Notenmenge, auf die eine Batch-Aktion wirkt: die Mehrfachauswahl, sonst die primäre Note. */
    function selectionInfos(): NoteInfo[] {
        if (selectedNotes.value.length) return selectedNotes.value;
        return selectedNote.value ? [selectedNote.value] : [];
    }

    /**
     * Lässt die Tonhöhen hören, die eine Bearbeitung hinterlassen hat – gelesen aus dem **neuen**
     * XML, damit wirklich das Ergebnis erklingt und nicht der Ausgangszustand.
     *
     * Bewusst NICHT in `commit()`: dort laufen auch Tonart-, Taktart-, Akkordsymbol- und
     * Lösch-Aktionen durch, die keinen Ton rechtfertigen.
     */
    function soundEdited(
        xml: string | null | undefined, infos: readonly NoteInfo[], includeChordNeighbours = false,
    ): void {
        if (!xml || !infos.length) return;
        const midiKeys = readSoundingPitches(xml, infos, {includeChordNeighbours});
        if (midiKeys.length) previewTone(midiKeys);
    }

    /**
     * Lässt eine eingefügte Sequenz hören. Die Tonhöhen kommen aus der **Zwischenablage**, nicht aus
     * dem XML: beim Einfügen landen die neuen Noten hinter der Auswahl, `selectedNote` zeigt also
     * weiter auf die unveränderte Ausgangsnote – ein Auslesen über die Auswahl spielte den falschen
     * Ton.
     */
    function soundClipboard(notes: readonly ClipboardNote[]): void {
        const midiKeys = notes
            .filter(n => !n.isRest && n.step)
            .map(n => pitchToMidi(n.step, n.alter, n.octave));
        if (midiKeys.length) previewTone(midiKeys);
    }

    function clearSelectionState(): void {
        selectedMeasureIndex.value = null;
        selectedMeasure.value = null;
        selectedNote.value = null;
        selectedNotes.value = [];
        selectedMeasureIndices.value = [];
        selectedMeasures.value = [];
        clickedGraphicalNote.value = null;
    }

    /** Löscht die aktuelle Auswahl. **Voll über alle Parts ausgewählte Takte** (`selectedMeasures`;
     *  abgeleitet, siehe `fullySelectedMeasures`) werden **wirklich** score-weit entfernt (Stück wird
     *  kürzer, `deleteMeasuresInMusicXml`); alle **übrigen** ausgewählten Noten (Teilauswahl, auch bei
     *  gemischter Auswahl) werden längenerhaltend in Pausen umgewandelt (keine MeterException). Bei
     *  gemischter Auswahl **zuerst** die Teilnoten zu Pausen (Original-Taktnummern), **dann** die vollen
     *  Takte entfernen (`deleteMeasuresInMusicXml` renummeriert). Auswahl wird danach geleert. */
    function deleteSelection(): void {
        if (!musicXml.value) return;
        const infos = selectionInfos();
        const fullMeasureIndices = selectedMeasureIndices.value;
        if (fullMeasureIndices.length) {
            let xml: string | null = musicXml.value;
            // Noten außerhalb der voll ausgewählten Takte zuerst zu Pausen (vor der Renummerierung).
            const partial = infos.filter(i => i.measureIndex === undefined || !fullMeasureIndices.includes(i.measureIndex));
            if (partial.length) xml = deleteNotesInMusicXml(xml, partial);
            if (xml) xml = deleteMeasuresInMusicXml(xml, [], fullMeasureIndices);
            commit(xml);
            clearSelectionState();
            return;
        }

        if (!infos.length) return;
        const updatedXml = deleteNotesInMusicXml(musicXml.value, infos);
        commit(updatedXml);
        clearSelectionState();
    }

    // „Note teilen": halbiert die Note und fügt eine zweite gleicher Tonhöhe direkt danach ein
    // (längenerhaltend). Auswahl bleibt auf der (jetzt halbierten) Originalnote – ihr
    // noteIndex/voiceNoteIndex ändert sich nicht, restoreSelectionHighlight markiert sie nach dem
    // Re-Render korrekt.
    function splitNote(noteInfo: NoteInfo): void {
        if (!musicXml.value) return;
        const updatedXml = splitNoteInMusicXml(musicXml.value, noteInfo);
        commit(updatedXml);
    }

    // „Zusammenführen" (Gegenstück zum Teilen): führt die Note/Pause mit der direkt folgenden derselben
    // Stimme im selben Takt zu EINER Note mit der Summendauer zusammen (längenerhaltend). Auswahl bleibt
    // wie beim Teilen auf der (jetzt längeren) Note – ihr noteIndex/voiceNoteIndex ändert sich nicht.
    function mergeNote(noteInfo: NoteInfo): void {
        if (!musicXml.value) return;
        const updatedXml = mergeNoteWithNextInMusicXml(musicXml.value, noteInfo);
        commit(updatedXml);
    }

    // ============ Kopieren / Einfügen (Strg+C / Strg+V / Strg+Umschalt+V) ============

    /** Strg+Umschalt+V: ersetzt die Auswahl durch die kopierte Sequenz. **Einzelauswahl** → die eine
     *  Note/Pause wird durch die **ganze Sequenz inkl. Rhythmus** ersetzt (Viertel → 2 Achtel;
     *  `replaceNoteWithSequenceInMusicXml`, taktlängen-neutral). **Mehrfachauswahl** → **zyklisches
     *  Tonhöhen-Ersetzen** (`clips[i % len]`, Dauer bleibt; `replaceNotesPitchesInMusicXml`). Bei
     *  Einzelauswahl + Einzelkopie gleicher Dauer degradiert es exakt zum reinen Tonhöhen-Ersetzen. */
    function pasteNoteReplace(): void {
        if (!musicXml.value || !copiedSequence.value.length) return;
        const infos = selectionInfos();
        if (!infos.length) return;
        const updatedXml = infos.length === 1
            ? replaceNoteWithSequenceInMusicXml(musicXml.value, infos[0]!, copiedSequence.value)
            : replaceNotesPitchesInMusicXml(musicXml.value, infos, copiedSequence.value);
        commit(updatedXml);
        if (updatedXml) soundClipboard(copiedSequence.value);
    }

    /** Kopiert die gesamte (Mehrfach-)Auswahl als geordnete Sequenz in die Zwischenablage (Strg+C). */
    function copySelection(): void {
        if (!musicXml.value) return;
        const infos = selectionInfos();
        if (!infos.length) return;
        copiedSequence.value = readNotesPayload(musicXml.value, infos);
    }

    /** Fügt die kopierte Sequenz direkt nach der primären Auswahl ein (Strg+V, taktlängen-neutral). */
    function pasteSelection(): void {
        if (!musicXml.value || !selectedNote.value || !copiedSequence.value.length) return;
        const updatedXml = insertSequenceAfterInMusicXml(musicXml.value, selectedNote.value, copiedSequence.value);
        commit(updatedXml);
        if (updatedXml) soundClipboard(copiedSequence.value);
    }

    /** Transponiert die gesamte (Mehrfach-)Auswahl um ±`deltaSemitones` Halbtöne (Pausen übersprungen,
     *  Dauer unberührt → keine MeterException). Auswahl bleibt erhalten. */
    function transposeSelection(deltaSemitones: number): void {
        if (!musicXml.value) return;
        const infos = selectionInfos();
        if (!infos.length) return;
        const updatedXml = transposeNotesInMusicXml(musicXml.value, infos, deltaSemitones);
        commit(updatedXml);
        soundEdited(updatedXml, infos);
    }

    // ============ Diatonisch (Akkordton hinzufügen / Dreiklang reduzieren) ============

    /** Übernimmt ein Ergebnis mit Index-Verschiebungen: neues XML setzen und die Auswahl remappen
     *  (`voiceNoteIndex` verschoben, `noteIndex` als veraltet markiert – wird nach dem Re-Render in
     *  `restoreSelectionHighlight` neu abgeleitet). Die primäre Note folgt per `noteKey`; wurde sie
     *  entfernt, wird die erste verbleibende Note primär. */
    /**
     * Einziger Commit-Punkt der Edit-Aktionen.
     *
     * Markiert dabei angefasste Takte des Bass-Vorschau-Parts als Handarbeit – bernstein statt
     * violett –, damit das Neuerzeugen sie stehen lässt und man im Notenbild sieht, welche Takte
     * die Bass-Zeile der Griffschrift bestimmen (siehe `markBassPreviewMeasuresAsEdited`). Alle
     * Aktionen gehen hier durch – eine zusätzliche Zuweisung an `musicXml.value` umginge das.
     */
    function commit(xml: string | null | undefined, touched?: NoteInfo[]): void {
        if (!xml) return;
        const infos = touched
            ?? (selectedNotes.value.length ? selectedNotes.value
                : selectedNote.value ? [selectedNote.value] : []);
        musicXml.value = markBassPreviewMeasuresAsEdited(xml, infos);
    }

    function applyIndexEditResult(result: DiatonicEditResult | null, infos: NoteInfo[]): void {
        if (!result) return;
        const remapped = remapSelectionAfterIndexEdits(infos, result.edits);
        // Primäre Note separat remappen und über ihren (remappten) noteKey wiederfinden.
        const primary = selectedNote.value
            ? remapSelectionAfterIndexEdits([selectedNote.value], result.edits)[0]
            : undefined;
        const primaryKey = primary ? noteKey(primary) : null;
        commit(result.xml, infos);
        selectedNotes.value = remapped;
        selectedNote.value = remapped.find(i => noteKey(i) === primaryKey) ?? remapped[0] ?? null;
        // Nach dem Remap zeigen die verschobenen Adressen auf das neue XML. Hier – und nur hier –
        // klingt die ganze Akkord-Gruppe: der soeben eingefügte Ton steht nicht in der Auswahl, ist
        // aber genau das, was man hören will (siehe `readSoundingPitches`).
        soundEdited(result.xml, remapped, true);
    }

    /** „Diatonisch hinzufügen": fügt zu jeder ausgewählten Note den um `interval` (±2/3/5/6/8)
     *  diatonisch verschobenen Ton als Akkord-Begleiter hinzu (Tonart = geltende `<fifths>`;
     *  nicht-diatonische Noten/Pausen/Duplikate übersprungen; taktlängen-neutral). */
    function addDiatonicTones(interval: number): void {
        if (!musicXml.value) return;
        const infos = selectionInfos();
        if (!infos.length) return;
        applyIndexEditResult(addDiatonicChordTonesInMusicXml(musicXml.value, infos, interval), infos);
    }

    /** „Dreiklang reduzieren": entfernt aus jeder ausgewählten Akkord-Gruppe mit genau drei Tönen den
     *  nicht genannten Ton (`low_mid`/`low_high`/`mid_high`); Dyaden/Vierklänge bleiben unberührt. */
    function reduceTriads(mode: TriadReductionMode): void {
        if (!musicXml.value) return;
        const infos = selectionInfos();
        if (!infos.length) return;
        applyIndexEditResult(reduceTriadsInMusicXml(musicXml.value, infos, mode), infos);
    }

    /** „Höchste/Niedrigste Note entfernen": entfernt aus jeder ausgewählten Akkord-Gruppe den
     *  höchsten bzw. tiefsten Ton – größenunabhängig (Zweiklang → Einzelton, Vierklang →
     *  Dreiklang); einzelne Noten bleiben unberührt. */
    function removeExtremeChordTones(which: ChordToneExtreme): void {
        if (!musicXml.value) return;
        const infos = selectionInfos();
        if (!infos.length) return;
        applyIndexEditResult(removeExtremeChordTonesInMusicXml(musicXml.value, infos, which), infos);
    }

    // ============ Bindebogen (Strg+B) ============

    /** Toggelt einen Bindebogen (Tie bei gleicher, Slur bei unterschiedlicher Tonhöhe) zwischen der
     *  Auswahl und der direkt folgenden spielbaren Note. Auswahl bleibt erhalten. */
    function toggleTie(): void {
        if (!musicXml.value || !selectedNote.value || selectedNote.value.isRest) return;
        const updatedXml = toggleTieOrSlurInMusicXml(musicXml.value, selectedNote.value);
        commit(updatedXml);
    }

    // ============ Akkord / Tonart ============

    function saveChord(data: { measureNumber: number; measureIndex?: number; chord: string }): void {
        if (!musicXml.value) return;
        const updatedXml = updateChordInMusicXml(
            musicXml.value, data.measureNumber, data.chord, undefined, data.measureIndex,
        );
        commit(updatedXml);
    }

    function removeChord(measureNumber: number, measureIndex?: number): void {
        if (!musicXml.value) return;
        const updatedXml = updateChordInMusicXml(
            musicXml.value, measureNumber, null, undefined, measureIndex,
        );
        commit(updatedXml);
    }

    function saveKeySignature(keyInfo: KeySignatureInfo): void {
        if (!musicXml.value) return;
        const updatedXml = updateKeySignatureInMusicXml(
            musicXml.value, keyInfo.measureNumber, keyInfo.fifths, keyInfo.mode, keyInfo.measureIndex,
        );
        commit(updatedXml);
    }

    /** Setzt die Taktart (reine Notations-Korrektur, score-weit). Defensiver Guard: würde das Metrum
     *  den Takt überfüllen, wird NICHT geschrieben (sonst MeterException beim Export). Die View blockt
     *  denselben Fall bereits + warnt; dies ist die zweite Verteidigungslinie. */
    function saveTimeSignature(timeInfo: TimeSignatureInfo): void {
        if (!musicXml.value) return;
        if (measureIsOverfullForMeter(
            musicXml.value, timeInfo.measureNumber, timeInfo.beats, timeInfo.beatType, timeInfo.measureIndex,
        )) return;
        const updatedXml = updateTimeSignatureInMusicXml(
            musicXml.value, timeInfo.measureNumber, timeInfo.beats, timeInfo.beatType, timeInfo.measureIndex,
        );
        commit(updatedXml);
    }

    // ============ Struktur (Taktstriche / Wiederholungen / Volta / Takt einfügen+löschen) ============

    /** Gemeinsamer Rumpf der Takt-Struktur-Aktionen: wirkt auf den ausgewählten Takt und committet
     *  mit `touched = []` – Struktur-Edits fassen keine Noten an, also keine Bass-Handarbeits-
     *  Markierung (`markBassPreviewMeasuresAsEdited` bliebe sonst an der zufälligen Notenauswahl
     *  hängen). */
    function withMeasureIndex(fn: (xml: string, measureIndex: number) => string | null): void {
        const idx = selectedMeasureIndex.value;
        if (!musicXml.value || idx === null) return;
        commit(fn(musicXml.value, idx), []);
    }

    function setRepeat(side: 'start' | 'end', enabled: boolean, times?: number): void {
        withMeasureIndex((xml, idx) => setRepeatInMusicXml(xml, idx, side, enabled, times));
    }

    function setRepeatTimes(times: number | null): void {
        withMeasureIndex((xml, idx) => setRepeatTimesInMusicXml(xml, idx, times));
    }

    function setBarStyle(style: BarStyleChoice): void {
        withMeasureIndex((xml, idx) => setBarStyleInMusicXml(xml, idx, style));
    }

    function setEnding(endingNumber: number, startIndex: number, endIndex: number): void {
        if (!musicXml.value) return;
        commit(setEndingInMusicXml(musicXml.value, startIndex, endIndex, endingNumber), []);
    }

    function removeEnding(): void {
        withMeasureIndex((xml, idx) => removeEndingInMusicXml(xml, idx));
    }

    /** Gedruckte Taktnummer eines Takts im (frischen) XML – für den Takt-Kontext nach dem Einfügen. */
    function measureNumberAt(xml: string, measureIndex: number): number | null {
        try {
            const part = getParts(parseMusicXml(xml))[0];
            const measure = part ? findMeasureInPartByIndex(part, measureIndex) : null;
            const num = measure?.getAttribute('number');
            return num === null || num === undefined ? null : parseInt(num, 10);
        } catch {
            return null;
        }
    }

    /** Fügt einen pausengefüllten Takt vor/nach dem ausgewählten ein. Die Notenauswahl wird
     *  verworfen (Indizes verschieben sich score-weit), der **Takt-Kontext bleibt** auf dem neu
     *  eingefügten Takt (`before` → alter Index, `after` → Index+1). */
    function insertMeasure(position: 'before' | 'after'): void {
        const idx = selectedMeasureIndex.value;
        if (!musicXml.value || idx === null) return;
        const updatedXml = insertMeasureInMusicXml(musicXml.value, idx, position);
        if (!updatedXml) return;
        commit(updatedXml, []);
        const newIndex = position === 'before' ? idx : idx + 1;
        selectedNote.value = null;
        selectedNotes.value = [];
        clickedGraphicalNote.value = null;
        // Ändert sich der Index ('after'), die Auto-Notenwahl des Takt-Watches einmalig
        // unterdrücken – sie liefe gegen das noch alte Render (siehe Watch in useScoreSelection).
        if (selectedMeasureIndex.value !== newIndex) suppressMeasureAutoSelect.value = true;
        selectedMeasureIndex.value = newIndex;
        selectedMeasure.value = measureNumberAt(updatedXml, newIndex);
    }

    /** Löscht den ausgewählten Takt score-weit (Spiegel des Ganztakt-Pfads in `deleteSelection`). */
    function deleteMeasure(): void {
        const idx = selectedMeasureIndex.value;
        if (!musicXml.value || idx === null) return;
        commit(deleteMeasuresInMusicXml(musicXml.value, [], [idx]), []);
        clearSelectionState();
    }

    return {
        deleteSelection,
        splitNote,
        mergeNote,
        copySelection,
        pasteNoteReplace,
        pasteSelection,
        transposeSelection,
        addDiatonicTones,
        reduceTriads,
        removeExtremeChordTones,
        toggleTie,
        saveChord,
        removeChord,
        saveKeySignature,
        saveTimeSignature,
        setRepeat,
        setRepeatTimes,
        setBarStyle,
        setEnding,
        removeEnding,
        insertMeasure,
        deleteMeasure,
    } as const;
}

export type ScoreEditActions = ReturnType<typeof useScoreEditActions>;
